import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, FolderPlus, RotateCcw, CheckCircle2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { fmtGBP } from './LineItemsTable';
import { fmtUKDate } from './inboxFormat';

/**
 * Manage the lightweight ad-hoc jobs created from invoice processing. Close one
 * when the work is done — it leaves the job pickers and Projects list; already
 * posted ledger costs are untouched. Closed jobs can be re-opened.
 */
export function AdhocProjectsDialog({ api, open, onOpenChange, onChanged }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showClosed, setShowClosed] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/cost-inbox/adhoc-projects?include_closed=${showClosed}`);
      setRows(r.data.projects || []);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not load ad-hoc jobs'); }
    finally { setLoading(false); }
  }, [api, showClosed]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const act = async (id, action) => {
    setBusyId(id);
    try {
      await api.post(`/cost-inbox/adhoc-projects/${id}/${action}`);
      toast.success(action === 'close' ? 'Ad-hoc job closed' : 'Ad-hoc job re-opened');
      await load(); onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not update'); }
    finally { setBusyId(null); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent aria-describedby={undefined} className="bg-[#1E2530] border-white/10 text-white max-w-2xl" data-testid="adhoc-manager-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><FolderPlus className="w-5 h-5 text-[#F5A623]" /> Ad-hoc jobs</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-gray-400 -mt-1">Lightweight jobs for pooling minor invoices. Close one when the work's done — it leaves the job pickers and Projects list; posted costs stay on the ledger.</p>
        <div className="flex items-center justify-end">
          <label className="flex items-center gap-2 text-xs text-gray-300 cursor-pointer select-none">
            <input type="checkbox" checked={showClosed} onChange={e => setShowClosed(e.target.checked)} className="accent-[#F5A623] w-3.5 h-3.5" data-testid="adhoc-show-closed" /> Show closed
          </label>
        </div>
        <div className="max-h-[55vh] overflow-y-auto space-y-2" data-testid="adhoc-list">
          {loading ? (
            <div className="text-center py-8"><Loader2 className="w-5 h-5 animate-spin text-gray-400 mx-auto" /></div>
          ) : rows.length === 0 ? (
            <div className="text-center py-8 text-gray-500 text-sm" data-testid="adhoc-empty">No ad-hoc jobs{showClosed ? '' : ' yet — create one from an invoice’s job picker'}.</div>
          ) : rows.map(p => (
            <div key={p.id} className={`rounded-lg border p-3 flex items-center gap-3 ${p.closed ? 'border-white/5 bg-white/[0.02] opacity-70' : 'border-white/10 bg-[#2A3240]'}`} data-testid={`adhoc-row-${p.id}`}>
              <div className="min-w-0 flex-1">
                <div className="font-medium truncate flex items-center gap-2">
                  <span className={`text-xs px-1.5 py-0.5 rounded shrink-0 ${p.company === 'CON' ? 'bg-sky-500/20 text-sky-300' : 'bg-[#F5A623]/20 text-[#F5A623]'}`}>{p.project_ref}</span>
                  <span className="truncate">{p.name}</span>
                  {p.closed && <span className="text-[10px] text-gray-400 inline-flex items-center gap-1 shrink-0"><CheckCircle2 className="w-3 h-3" /> closed</span>}
                </div>
                <div className="text-[11px] text-gray-400 mt-0.5">{p.line_count} line{p.line_count === 1 ? '' : 's'} · {fmtGBP(p.value_pence)} · created {fmtUKDate(p.created_at)}{p.created_by_name ? ` by ${p.created_by_name}` : ''}</div>
              </div>
              {p.closed ? (
                <Button size="sm" variant="outline" disabled={busyId === p.id} onClick={() => act(p.id, 'reopen')} className="border-white/20 text-white bg-white/5 hover:bg-white/10 shrink-0" data-testid={`adhoc-reopen-${p.id}`}>
                  {busyId === p.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><RotateCcw className="w-3.5 h-3.5 mr-1" /> Re-open</>}
                </Button>
              ) : (
                <Button size="sm" variant="outline" disabled={busyId === p.id} onClick={() => act(p.id, 'close')} className="border-emerald-500/40 text-emerald-200 bg-emerald-500/10 hover:bg-emerald-500/20 shrink-0" data-testid={`adhoc-close-${p.id}`}>
                  {busyId === p.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Close</>}
                </Button>
              )}
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
